import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ContaService } from '../../../../../services/conta/conta.service';
import { EmpresaConfigService } from '../../../../../services/configuracoes/empresa.service';
import { EmpresaConfigDTO } from '../../../../../models/configuracoes/empresa.model';
import { TipoConta } from '../../../../../enums/TipoConta.enum';
import { Moeda } from '../../../../../enums/moeda.enum';
import { ContaDTO, ContaFormDTO } from '../../../../../models/gerenciar-contas/conta.model';
import { moedasCompativeis } from '../../../../../utils/moeda-pareamento.util';

@Component({
  selector: 'app-nova-conta-edit',
  templateUrl: './nova-conta-edit.component.html',
  styleUrls: ['./nova-conta-edit.component.scss'],
  providers: [MessageService],
})
export class NovaContaEditComponent implements OnInit, OnDestroy {
  form: ContaFormDTO = this.createEmptyForm();
  tipoConta: { label: string; value: string }[] = [];
  moedas: { label: string; value: string; flagUrl: string }[] = [];
  idEdit: number | undefined;
  isFormSubmitted = false;

  // Empresa logada — usada para filtrar moedas pelo pareamento permitido
  empresa: EmpresaConfigDTO | null = null;

  // Textos traduzidos
  texts = {
    editarConta: '',
    subtitulo: '',
    dadosDaConta: '',
    valores: '',
    informacoesAdicionais: '',
  };

  labels = {
    tituloDaConta: '',
    numeroDaConta: '',
    tipoDeConta: '',
    moeda: '',
    saldo: '',
    descricao: '',
  };

  placeholders = {
    tituloDaConta: '',
    numeroDaConta: '',
    selecione: '',
    saldo: '',
    descricao: '',
  };

  botoes = {
    voltar: '',
    salvar: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private contaService: ContaService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private empresaService: EmpresaConfigService
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.buildTipoContaOptions();
    this.buildMoedaOptions();

    // Carrega empresa logada para filtrar moedas pelo pareamento permitido
    this.empresaService.getSessao().subscribe({
      next: (config) => {
        this.empresa = config;
        this.buildMoedaOptions(); // re-monta dropdown ja com filtro aplicado
      },
      error: () => {
        this.empresa = null;
        this.buildMoedaOptions();
      },
    });

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.idEdit = Number(id);
      this.loadConta(this.idEdit);
    }

    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadTranslations();
      this.buildTipoContaOptions();
      this.buildMoedaOptions();
    });
  }

  ngOnDestroy() {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations() {
    this.texts = {
      editarConta: this.translate.instant('nova-conta-edit.texts.editar_conta'),
      subtitulo: this.translate.instant('nova-conta-edit.texts.subtitulo'),
      dadosDaConta: this.translate.instant('nova-conta-edit.texts.dados_da_conta'),
      valores: this.translate.instant('nova-conta-edit.texts.valores'),
      informacoesAdicionais: this.translate.instant('nova-conta-edit.texts.informacoes_adicionais'),
    };

    this.labels = {
      tituloDaConta: this.translate.instant('nova-conta-edit.labels.titulo_da_conta'),
      numeroDaConta: this.translate.instant('nova-conta-edit.labels.numero_da_conta'),
      tipoDeConta: this.translate.instant('nova-conta-edit.labels.tipo_de_conta'),
      moeda: this.translate.instant('nova-conta-edit.labels.moeda'),
      saldo: this.translate.instant('nova-conta-edit.labels.saldo'),
      descricao: this.translate.instant('nova-conta-edit.labels.descricao'),
    };

    this.placeholders = {
      tituloDaConta: this.translate.instant('nova-conta-edit.placeholders.titulo_da_conta'),
      numeroDaConta: this.translate.instant('nova-conta-edit.placeholders.numero_da_conta'),
      selecione: this.translate.instant('nova-conta-edit.placeholders.selecione'),
      saldo: this.translate.instant('nova-conta-edit.placeholders.saldo'),
      descricao: this.translate.instant('nova-conta-edit.placeholders.descricao'),
    };

    this.botoes = {
      voltar: this.translate.instant('nova-conta-edit.botoes.voltar'),
      salvar: this.translate.instant('nova-conta-edit.botoes.salvar'),
    };
  }

  private buildTipoContaOptions() {
    this.tipoConta = Object.values(TipoConta)
      .filter(tipo => tipo !== TipoConta.DINHEIRO)
      .map(tipo => ({
        label: this.translate.instant(`enums.tipo_conta.${tipo.toLowerCase()}`),
        value: String(tipo).toUpperCase(),
      }));
  }

  /**
   * Conjunto de moedas permitidas para a empresa logada, baseado na
   * matriz de pareamento (moedasCompativeis). Usado para filtrar o
   * dropdown de moedas na edicao de conta.
   *
   * Fallback: enquanto a empresa nao carregou, retorna todas as 4
   * moedas para nao bloquear a UI.
   */
  get moedasPermitidas(): Set<string> {
    const moedaEmp = this.empresa?.moeda
      ? ((this.empresa.moeda as any).enum ?? this.empresa.moeda) as string
      : null;
    if (!moedaEmp) return new Set(['GUARANI', 'DOLAR', 'REAL', 'PESO']);
    return new Set(moedasCompativeis(moedaEmp as any));
  }

  private buildMoedaOptions() {
    const permitidas = this.moedasPermitidas;
    // Sempre incluir a moeda da conta sendo editada (mesmo fora da matriz),
    // para nao quebrar contas legacy salvas com moedas hoje incompativeis.
    const moedaAtual = this.form?.moeda?.value
      ? String(this.form.moeda.value).toUpperCase()
      : null;

    this.moedas = Object.values(Moeda)
      .filter(moeda => {
        const m = String(moeda).toUpperCase();
        return permitidas.has(m) || (moedaAtual !== null && m === moedaAtual);
      })
      .map(moeda => ({
        label: this.translate.instant(`enums.moeda.${moeda.toLowerCase()}`),
        value: String(moeda).toUpperCase(),
        flagUrl: this.getFlagUrl(moeda),
      }));
  }

  getFlagUrl(moeda: any): string {
    const moedaStr = String(moeda || '').toUpperCase();
    switch (moedaStr) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  get selectedLocale(): string {
    if (!this.form.moeda) return 'es-PY';
    switch (this.form.moeda.value) {
      case 'REAL': return 'pt-BR';
      case 'DOLAR': return 'en-US';
      case 'GUARANI': return 'es-PY';
      default: return 'es-PY';
    }
  }

  get selectedCasasDecimais(): number {
    if (!this.form.moeda) return 0;
    return this.form.moeda.value === 'GUARANI' ? 0 : 2;
  }

  loadConta(id: number) {
  /*  this.contaService.getById(`/conta/${id}`).subscribe({
      next: (response) => {
        const tipoConta = this.tipoConta.find((t) => t.value === response.tipoConta);
        const moeda = this.moedas.find((m) => m.value === response.moeda);

        this.form = {
          id: response.id,
          descricao: response.descricao,
          moeda: moeda
            ? { label: moeda.label.toUpperCase(), value: moeda.value }
            : undefined,
          numeroConta: response.numeroConta,
          saldo: response.saldo,
          tipoConta: tipoConta
            ? { label: tipoConta.label.toUpperCase(), value: tipoConta.value }
            : undefined,
          tituloDaConta: response.tituloDaConta,
          valorTotalDeEntradas: response.valorTotalDeEntradas,
          valorTotalDeSaidas: response.valorTotalDeSaidas,
          aceitaTarjetaQrcode: response.aceitaTarjetaQrcode === true,
        };
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('nova-conta-edit.validacoes.erro_carregar'),
        });
        this.router.navigate(['/conta']);
      },
    });*/
  }

  navigateBack(): void {
    this.router.navigate(['/conta']);
  }

  submit() {
    this.isFormSubmitted = true;
    if (!this.isValidForm()) {
      return;
    }

    const body: ContaDTO = {
      id: this.idEdit,
      descricao: this.form.descricao!,
      moeda: this.form.moeda!.value as Moeda,
      numeroConta: this.form.numeroConta,
      saldo: this.form.saldo!,
      tipoConta: this.form.tipoConta!.value as TipoConta,
      tituloDaConta: this.form.tituloDaConta!,
      // Acumuladores movimentados pelo sistema: seguem como vieram do servidor, sem passar pela
      // tela — nao ha campo para o usuario alterar.
      valorTotalDeEntradas: this.form.valorTotalDeEntradas ?? 0,
      valorTotalDeSaidas: this.form.valorTotalDeSaidas ?? 0,
      aceitaTarjetaQrcode: this.form.aceitaTarjetaQrcode,
      // Os dados bancarios da empresa nao sao mostrados ao cliente no Mini Shop.
      exibirParaTransferencia: false,
    };

    this.contaService.put(`/conta/${this.idEdit}`, body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Conta_atualizada_com_sucesso'),
        });
        this.router.navigate(['/conta']);
      },
    });
  }

  isValidForm(): boolean {
    const requiredFields: (keyof ContaFormDTO)[] = [
      'descricao',
      'moeda',
      'saldo',
      'tipoConta',
      'tituloDaConta',
    ];

    for (const field of requiredFields) {
      const fieldValue = this.form[field];

      if (
        fieldValue === null ||
        fieldValue === undefined ||
        (typeof fieldValue === 'string' && fieldValue.trim() === '')
      ) {
        const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1);
        const detail = this.translate.instant('nova-conta-edit.validacoes.campo_obrigatorio', { field: fieldLabel });

        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: detail,
        });
        return false;
      }
    }
    return true;
  }

  private createEmptyForm(): ContaFormDTO {
    // Tipo Conta padrão: BANCO (campo desabilitado na tela)
    const tipoBanco = this.tipoConta?.find(t => t.value === TipoConta.BANCO);
    return {
      descricao: '',
      moeda: undefined,
      numeroConta: '',
      saldo: null,
      tipoConta: tipoBanco
        ? { label: tipoBanco.label, value: tipoBanco.value }
        : { label: 'Banco', value: TipoConta.BANCO },
      tituloDaConta: '',
      valorTotalDeEntradas: null,
      valorTotalDeSaidas: null,
      aceitaTarjetaQrcode: false,
    };
  }
}
